import { processListingParams } from '@/api/helpers';
import { ListingRequest, ListingResponse } from '@/api/types';
import { getExceptionFromAxiosError } from '@/client/helpers';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { CourseAttempt } from '../courses';
import { MessageResponseModel } from '../models';
import {
  CloneQuizRequest,
  DeleteQuizRequest,
  ListQuizzesResponse,
  Quiz,
  QuizAnswerStatistics,
  SurveySubmission,
} from './types';

export const quizzesService = (client: AxiosInstance) => {
  const QUIZZES_BASE_URL = '/quizzes';
  const SURVEY_QUIZZES_BASE_URL = (accountId: string) =>
    `accounts/${accountId}/survey-quizzes`;
  const SURVEY_BASE_URL = '/survey';
  const ME_QUIZZES_BASE_URL = '/me/quizzes';

  const getQuiz = async (accountId: string, quizId: string): Promise<Quiz> => {
    try {
      const result = await client.get<Quiz, AxiosResponse<Quiz>>(
        `${SURVEY_QUIZZES_BASE_URL(accountId)}/${quizId}`,
        {
          withCredentials: true,
        },
      );
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getQuizzes = async (
    accountId: string,
    params: ListingRequest,
  ): Promise<ListQuizzesResponse> => {
    try {
      const result = await client.get<
        ListQuizzesResponse,
        AxiosResponse<ListQuizzesResponse>
      >(SURVEY_QUIZZES_BASE_URL(accountId), {
        withCredentials: true,
        params: processListingParams(params),
      });
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const createQuiz = async (quiz: Quiz): Promise<Quiz> => {
    try {
      const result = await client.post<Quiz, AxiosResponse<Quiz>>(
        QUIZZES_BASE_URL,
        quiz,
        {
          withCredentials: true,
        },
      );
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const updateQuiz = async (quizId: string, quiz: Quiz): Promise<Quiz> => {
    try {
      const result = await client.put<Quiz, AxiosResponse<Quiz>>(
        `${QUIZZES_BASE_URL}/${quizId}`,
        quiz,
        {
          withCredentials: true,
        },
      );
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const deleteQuiz = async (
    request: DeleteQuizRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.delete(
        `${SURVEY_QUIZZES_BASE_URL(request.accountId)}/${request.quizId}`,
        {
          withCredentials: true,
        },
      );

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const cloneQuiz = async (payload: CloneQuizRequest): Promise<Quiz> => {
    try {
      const result = await client.post<Quiz, AxiosResponse<Quiz>>(
        `${SURVEY_QUIZZES_BASE_URL(payload.account)}/copy-master-quiz`,
        payload,
        {
          withCredentials: true,
        },
      );
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getLastQuizAttempt = async (quizId: string): Promise<any> => {
    try {
      const result = await client.get<
        CourseAttempt,
        AxiosResponse<CourseAttempt>
      >(`${ME_QUIZZES_BASE_URL}/${quizId}/last-attempt`, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const abandonQuizAttempt = async (
    quizId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${ME_QUIZZES_BASE_URL}/${quizId}/abandon-attempt`,
        {},
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getSurveyForUser = async (
    sentSurveyId: string,
    token?: string,
  ): Promise<Quiz> => {
    try {
      const result = await client.get<Quiz, AxiosResponse<Quiz>>(
        token
          ? `${SURVEY_BASE_URL}/${sentSurveyId}/${token}`
          : `${SURVEY_BASE_URL}/${sentSurveyId}`,
      );
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const submitSurveyForUser = async (
    payload: SurveySubmission,
    sentSurveyId: string,
    token?: string,
  ): Promise<Quiz> => {
    try {
      const result = await client.post<Quiz, AxiosResponse<Quiz>>(
        token
          ? `${SURVEY_BASE_URL}/${sentSurveyId}/${token}`
          : `${SURVEY_BASE_URL}/${sentSurveyId}`,
        payload,
      );
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getFeedbackQuizzes = async (): Promise<ListingResponse<Quiz>> => {
    try {
      const result = await client.get<
        ListingResponse<Quiz>,
        AxiosResponse<ListingResponse<Quiz>>
      >('/feedback-quizzes', {
        withCredentials: true,
      });
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getQuizzesAnswersStatistics = async (
    accountId: string,
    quizId: string,
  ): Promise<QuizAnswerStatistics[]> => {
    try {
      const result = await client.get<
        QuizAnswerStatistics[],
        AxiosResponse<QuizAnswerStatistics[]>
      >(`/accounts/${accountId}/reports/quiz-answers/${quizId}`, {
        withCredentials: true,
      });
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  return {
    getQuiz,
    getQuizzes,
    createQuiz,
    updateQuiz,
    deleteQuiz,
    cloneQuiz,
    getLastQuizAttempt,
    abandonQuizAttempt,
    getSurveyForUser,
    submitSurveyForUser,
    getFeedbackQuizzes,
    getQuizzesAnswersStatistics,
  };
};
